"use client";

// The deposit modal's whole body, as ONE screen.
//
// History, part 1 (2026-07-31, user): this file replaced `steps/SourceStep.tsx`
// + `steps/ExecuteStep.tsx`, the two halves of the old 2-step wizard:
//
//   "스텝 1·2가 아니라 스텝 없이 1장으로. 어차피 QR을 주고 사용자가 알아서
//    입금하는 거고 우리는 이걸 알 수 없다 — 대기/성공 팝업 불필요."
//
// The flow was never a commit-then-execute sequence: the deposit address is
// minted from (recipient, origin, target) alone, the amount is optional, and a
// manual send still has no local transaction receipt. It is now observed
// indirectly through the backend's orders SSE + 1s REST snapshots, without
// bringing the old step or full-screen completion state back.
//
// History, part 2 (2026-07-31, mockup "A안 · QR 퍼스트" then USER REVISION): the
// QR-first pass replaced the selectors with a one-line summary plus a `change ▾`
// disclosure, and the "Deliver as" field with a "You'll receive →" strip under
// the address. The user rejected both compressions -- hiding the controls made
// the choices look like read-outs -- so FROM and Deliver as are back as
// always-visible labelled rows at the top, exactly as they were before that
// pass. What SURVIVED from it is the part that consolidated ALARMS rather than
// hiding controls:
//
//   * the checklist card (`DepositChecklist.tsx`) -- minimum + only-this-asset /
//     reusable in one quiet card, replacing the `MinDepositNotice` block above
//     the address, DepositStepView's "Only send X on Y" callout below it, and its
//     "reusable address" sentence between them;
//   * the locked card in the QR's own slot for the disconnected state, replacing
//     the connect-prompt card that used to sit above the form;
//   * the long reusable-address caveat later moved into a tooltip beside the
//     Deposit address label; the detached footer/details blocks were removed.
//
// History, part 3 (2026-07-31, user): the address moved to the TOP and became one
// row with its QR, and the delivery token joined the recipient into a single
// statement. The column now reads:
//
//   live transfer status (only while useful)
//   QR | address + Copy   (or compact address ↔ QR toggle / locked card)
//   FROM (chain | token)  + optional amount / quote
//   Deliver as (picker)   + recipient address
//   checklist (send rules)
//
// Rationale, in the user's order of complaint: the thing every visit is FOR is the
// address, so only actionable live status may precede it; the QR and the chip are the same value twice,
// so they sit side by side rather than costing two vertical blocks; and "which
// token" + "where it lands" are one sentence, so the picker and the recipient are
// one block instead of two fields separated by the whole amount/quote area.
//
// Purely presentational: every value and handler comes from the wizard bundle
// via <RelayDepositContent>, which owns no state either -- see
// RelayWizardStateHost.tsx.
import type { Catalog, OriginOption, QuoteResult, Target } from "../lib/types";
import type { DepositState } from "../hooks/useDepositAddress";
import type { RelayBalance } from "../types";
import type { DepositArrivalStatus } from "../providers/relay-wizard-provider";
import { DepositChecklist, DepositStatus, type MinNoticeData } from "./DepositChecklist";
import { DepositAddressSkeleton, DepositStepView } from "./DepositStepView";
import { Dropdown } from "./Dropdown";
import { QuotePreview } from "./QuotePreview";
import { SourceForm } from "./SourceForm";
import { TokenIcon } from "./icons";
import "../styles/component.css";

export interface DepositPanelProps {
  catalog: Catalog;
  origin: OriginOption;
  onChangeOrigin: (origin: OriginOption) => void;

  amount: string;
  onChangeAmount: (value: string) => void;
  showAmountInput: boolean;
  /** Send-rules data for the checklist card under the address: the effective
   * minimum plus, when an earlier below-minimum deposit is still un-swept, the
   * pending / remaining pair that replaces the minimum line. */
  minNotice?: MinNoticeData;
  /** Inline warning under the amount input (below-minimum / above-maximum).
   * Advisory ONLY -- nothing on this screen is gated any more. */
  amountHint?: string;
  onMax?: () => void;

  recipient: string;
  onChangeRecipient: (value: string) => void;
  showRecipientInput: boolean;
  recipientValid: boolean;

  balance?: RelayBalance & { formatted: string };
  balanceLoading: boolean;
  insufficientBalance: boolean;

  /** Delivery targets offered, from useTargetTokens' `options`. */
  targetOptions: Target[];
  selectedTarget?: Target;
  onChangeTarget: (symbol: string) => void;
  /** The `showTargetSelector` prop; the picker still needs >1 option to render. */
  showTargetSelector: boolean;

  quote?: QuoteResult;
  quoteLoading: boolean;
  quoteError?: Error;

  /** The resolved deposit address; undefined while loading or failed. */
  depositAddress?: string;
  depositAddressLoading: boolean;
  depositAddressError?: Error;
  /** Live SSE-derived funding/delivery projection for this address. */
  arrivalStatus: DepositArrivalStatus;
  /** Smallest-unit base-10 integer string, or undefined when no amount was entered. */
  amountSmallestUnits?: string;

  depositState: DepositState;
  onDeposit: () => void;
  /** `useDepositAddress`'s own gate, already ANDed with the host's opt-in. */
  canDeposit: boolean;
  /** The `showOneClickDeposit` opt-in (default off) -- when false the address
   * section is manual-only and no in-flight progress card can appear. */
  oneClickEnabled: boolean;

  /** No identity yet (neither a connected wallet nor a usable recipient), so no
   * deposit address can exist: the QR slot renders a locked card + Connect CTA
   * instead. Replaces the old connect-prompt card above the form -- two "you're
   * not connected" surfaces on one screen said it twice. */
  locked: boolean;
  /** Wired to the locked card's `Connect Wallet` CTA. Omitted -- the locked card
   * still explains itself, with nothing to click (the recipient input, when
   * rendered, is a wallet-free path to an address either way). */
  onRequestConnect?: () => void;
}

export function DepositPanel({
  catalog,
  origin,
  onChangeOrigin,
  amount,
  onChangeAmount,
  showAmountInput,
  minNotice,
  amountHint,
  onMax,
  recipient,
  onChangeRecipient,
  showRecipientInput,
  recipientValid,
  balance,
  balanceLoading,
  insufficientBalance,
  targetOptions,
  selectedTarget,
  onChangeTarget,
  showTargetSelector,
  quote,
  quoteLoading,
  quoteError,
  depositAddress,
  depositAddressLoading,
  depositAddressError,
  arrivalStatus,
  amountSmallestUnits,
  depositState,
  onDeposit,
  canDeposit,
  oneClickEnabled,
  locked,
  onRequestConnect,
}: DepositPanelProps) {
  // Same condition as the source widget: a single/fixed delivery token renders
  // no picker regardless of `showTargetSelector`.
  const showPicker = targetOptions.length > 1 && showTargetSelector;
  const { phase, isSubmitting, isConfirming, isConfirmed, needsSwitch, reverted, error } = depositState;
  // A pre-multi-target catalog (`targets: []`) resolves no Target at all, but the
  // delivery token is still a fact the screen must state.
  const deliverSymbol = selectedTarget?.symbol ?? catalog.destination.symbol;

  return (
    <>
      {/* Live funding/delivery feedback stays at the very top of the panel so
          a newly detected transfer cannot be missed below the QR and fields.
          The static send rules remain in their supporting position below. */}
      {minNotice && <DepositStatus minNotice={minNotice} arrivalStatus={arrivalStatus} />}

      {/* ---- 1. QR + address (the top of the body) --------------------------
          Rendered the moment the address resolves -- no click in between. The
          loading skeleton has the exact same QR + address-card footprint, so an
          origin/target change cannot resize the modal while the new address is
          fetched. With no identity yet there is nothing to render but the reason
          why -- in the QR's own footprint, for the same reason. */}
      {locked ? (
        <section className="rd-locked-card" data-rd-locked="">
          <span className="rd-locked-glyph" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" focusable="false">
              <path
                d="M4 7.5A2.5 2.5 0 0 1 6.5 5h10A2.5 2.5 0 0 1 19 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-10A2.5 2.5 0 0 1 4 16.5v-9Z"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path d="M4 9h15" stroke="currentColor" strokeWidth="1.7" />
              <path
                d="M15 11.5h5v4h-5a2 2 0 0 1 0-4Z"
                fill="currentColor"
                opacity="0.18"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle cx="15.7" cy="13.5" r="0.8" fill="currentColor" />
            </svg>
          </span>
          <h3 className="rd-locked-title">Connect your wallet</h3>
          <p className="rd-locked-text">Connect to get your personal deposit address.</p>
          {showRecipientInput && (
            <p className="rd-card-hint">Or enter a recipient address to get one without connecting.</p>
          )}
          {onRequestConnect && (
            <button
              type="button"
              className="rd-button-primary rd-locked-action"
              data-track="connect"
              onClick={onRequestConnect}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 6.5h11.5A2.5 2.5 0 0 1 19 9v8.5H7A3 3 0 0 1 4 14.5v-8A2.5 2.5 0 0 1 6.5 4H16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M15 10.5h5v4h-5a2 2 0 0 1 0-4Z" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              Connect Wallet
            </button>
          )}
        </section>
      ) : depositAddress ? (
        <DepositStepView
          depositAddress={depositAddress}
          originChainId={origin.chainId}
          originChainName={origin.chainName}
          originCurrency={origin.token.address}
          amount={amountSmallestUnits}
          tokenSymbol={origin.token.symbol}
          tokenDecimals={origin.token.decimals}
          depositState={depositState}
          onDeposit={onDeposit}
          canDeposit={canDeposit}
          oneClickEnabled={oneClickEnabled}
        />
      ) : depositAddressLoading ? (
        <>
          <DepositAddressSkeleton />
          {oneClickEnabled && amountSmallestUnits && <span className="rd-skeleton rd-skeleton-cta" aria-hidden="true" />}
          <p className="rd-sr-only" role="status">
            Preparing your deposit address...
          </p>
        </>
      ) : depositAddressError ? (
        <div className="rd-callout rd-callout-danger">
          Couldn&apos;t fetch a deposit address: {depositAddressError.message}
        </div>
      ) : null}

      {/* ---- 2. FROM (+ the optional amount input) -------------------------- */}
      <SourceForm
        catalog={catalog}
        origin={origin}
        onChangeOrigin={onChangeOrigin}
        amount={amount}
        onChangeAmount={onChangeAmount}
        showAmountInput={showAmountInput}
        balance={balance}
        balanceLoading={balanceLoading}
        insufficientBalance={insufficientBalance}
        onMax={onMax}
      />

      {/* ---- 3. Deliver as + where it lands --------------------------------
          ONE block, because it is one sentence: which token you get, and the
          ONEchain address it is delivered to (user revision 2026-07-31 -- the
          delivery token and the recipient used to be two labelled fields with the
          whole amount/quote area between them, so nothing said they were about
          the same leg of the transfer). The picker is only ever a picker when
          there is actually more than one target to choose from. */}
      <div className="rd-field" data-rd-delivery="">
        <span className="rd-field-label">Deliver as</span>
        {showPicker ? (
          <Dropdown
            ariaLabel="Delivery token"
            value={selectedTarget?.symbol ?? ""}
            onChange={onChangeTarget}
            options={targetOptions.map((t) => ({
              value: t.symbol,
              label: t.symbol,
              subLabel: "on ONEchain",
              icon: <TokenIcon symbol={t.symbol} size={22} />,
            }))}
          />
        ) : (
          <div className="rd-static-target">
            <TokenIcon symbol={deliverSymbol} size={22} />
            <span>{deliverSymbol}</span>
          </div>
        )}

        {/* The recipient is entirely hidden in the normal fixed-recipient
            presentation. Only hosts that explicitly opt into recipient editing
            render an input here; no read-only address value is duplicated. */}
        {showRecipientInput && (
          <label className="rd-delivery-recipient">
            <input
              type="text"
              placeholder="0x..."
              aria-label="ONEchain recipient address"
              value={recipient}
              onChange={(e) => onChangeRecipient(e.target.value)}
              data-vaul-no-drag
            />
            <span className="rd-card-hint">
              Defaults to your connected wallet. It can differ from the address you deposit from, and is also used
              as the refund address if anything goes wrong.
            </span>
            {!recipientValid && <span className="rd-callout rd-callout-danger">Not a valid address</span>}
          </label>
        )}
      </div>

      {selectedTarget?.external && (
        <div className="rd-callout rd-callout-muted">
          Delivered as {selectedTarget.symbol} via an external adapter.
        </div>
      )}

      {/* Amount feedback, in reading order under the input: the hard
          below-min/above-max sentence first, then the live quote (whose own
          loading/error/belowMin states live inside it). Both are advisory --
          the deposit address below does not wait on either. */}
      {amountHint && (
        <div className="rd-callout rd-callout-warning" data-rd-amount-hint="">
          {amountHint}
        </div>
      )}

      {amount.trim() && (
        <QuotePreview
          quote={quote}
          isLoading={quoteLoading}
          error={quoteError}
          humanAmount={amount}
          originSymbol={origin.token.symbol}
          destination={catalog.destination}
        />
      )}

      {/* ---- Send rules -----------------------------------------------------
          The dynamic transfer status now lives at the top of this panel. This
          quiet rules card keeps its supporting position below the controls. */}
      {minNotice && (
        <DepositChecklist
          minNotice={minNotice}
          onlySend={{ symbol: origin.token.symbol, chainName: origin.chainName }}
          showWalletCreditNote={!showRecipientInput && recipientValid}
        />
      )}

      {/* One-click progress, inline under the address block that owns the
          button. DepositStepView hides its whole one-click block once
          `canDeposit` goes false -- which is exactly what a submit in flight
          does -- so without this a user who clicked would get no feedback at
          all. Strings are the ones DepositStepView itself uses. Never rendered
          in the default QR-only flow: there is no button to have clicked.
          Manual sends use the separate SSE-derived status slot above; this
          card remains specific to the locally submitted one-click tx.) */}
      {oneClickEnabled && phase !== "idle" && !canDeposit && (
        <div className="rd-card">
          {isSubmitting && (
            <p className="rd-muted">
              <span className="rd-spinner" /> {needsSwitch ? "Switching network..." : "Confirm in wallet..."}
            </p>
          )}

          {isConfirming && (
            <p className="rd-muted">
              <span className="rd-spinner" /> Confirming...
            </p>
          )}

          {depositState.txHash && (
            <p className="rd-card-hint">
              Tx: <span style={{ fontFamily: "monospace" }}>{depositState.txHash}</span>
            </p>
          )}

          {isConfirmed && reverted && (
            <div className="rd-callout rd-callout-danger">
              The deposit transaction reverted on-chain. Please check your wallet and try again.
            </div>
          )}

          {isConfirmed && !reverted && (
            <div className="rd-callout rd-callout-success">
              Deposit confirmed on-chain. Waiting for it to be swept...
            </div>
          )}

          {error && <div className="rd-callout rd-callout-danger">{error}</div>}
        </div>
      )}

    </>
  );
}
